// Onboard/CreateAccount.jsx
import React, { useState } from "react";
import axios from "axios";
import api from "../../utils/api";
// Gov.UK-style UK postcode (allows optional space, case-insensitive)
const UK_POSTCODE_RE =
  /^([Gg][Ii][Rr]\s?0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9]?[A-Za-z]))))\s?[0-9][A-Za-z]{2})$/;


// Simple UK phone regex
const UK_PHONE_RE = /^(\+?44|0)[\s-]?\d([\s-]?\d){8,12}$/;

export default function CreateAccount({ email_token, email, authority_name, onDone }) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    confirmPhone: "",
    password: "",
    confirmPassword: "",
    address: "",
    postcode: "",
    status_in_uk: "Citizen / ILR / Visa",
    cuisine_types: [],
    meal_types: [],
    price_categories: [],
  });
  const [msg, setMsg] = useState("");

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Toggle helper for multi-select arrays
  const toggleArrayVal = (key, val) => {
    setForm(f => ({
      ...f,
      [key]: f[key].includes(val)
        ? f[key].filter(v => v !== val)
        : [...f[key], val],
    }));
  };

  const validate = () => {
    if (!form.name.trim()) return "Name is required.";
    if (!UK_PHONE_RE.test(form.phone)) return "Enter a valid UK phone number.";
    if (form.phone !== form.confirmPhone) return "Phone numbers do not match.";
    if (form.address.trim().length < 5 || !/[A-Za-z]/.test(form.address)) {
	  return "Enter a valid address (e.g. 12 Market Street).";
	}
    if (!form.postcode.trim()) return "Postcode is required.";
    if (!UK_POSTCODE_RE.test(form.postcode.toUpperCase()))
       return "Enter a valid UK postcode (e.g. SW1A 1AA).";
    if (!form.price_categories.length) return "Select at least one price category.";
    if (!form.cuisine_types.length) return "Select at least one cuisine.";
    if (!form.meal_types.length) return "Select at least one meal type.";
    if (!form.password || form.password.length < 8)
      return "Password must be at least 8 characters.";
    if (form.password !== form.confirmPassword)
      return "Passwords do not match.";
    return "";
  };

  const submit = async () => {
    setMsg("");
    const err = validate();
    if (err) {
      setMsg(err);
      return;
    }
    try {
const body = {
  name: form.name,
  phone: form.phone,
  confirmPhone: form.confirmPhone,
  password: form.password,
  confirmPassword: form.confirmPassword,
  address: form.address,
  postcode: form.postcode,
  status_in_uk: form.status_in_uk,
  cuisine_types: form.cuisine_types,
  meal_types: form.meal_types,
  price_categories: form.price_categories,
  email,
  confirmEmail: email,
};


      const res = await api.post(`/auth/register`, body, {
        headers: { "x-email-token": email_token },
      });
      if (res.status === 201) {
        sessionStorage.removeItem("onb_email");
        sessionStorage.removeItem("onb_vid");
        onDone();
      }
    } catch (e) {
      setMsg(e?.response?.data?.error || "Registration failed");
    }
  };

  // Options
  const CUISINE_OPTIONS = [
    "Indian", "Pakistani", "Italian", "Chinese", "British",
    "Turkish", "Middle Eastern", "African", "Caribbean", "Other",
  ];
  const MEAL_TYPES = ["Breakfast", "Lunch", "Dinner"];
  const PRICE_OPTIONS = ["Value", "Healthy", "Premium"];

  return (
    <div className="onboard-wrapper">
      <div className="onboard-card">
        <section className="card" style={{ maxWidth: 820, margin: "2rem auto" }}>
          <h3>Create your account</h3>

          <form autoComplete="off" onSubmit={e => { e.preventDefault(); submit(); }}>
            <div className="grid two">
              <label>
                <input
                  placeholder="Full name"
                  value={form.name}
                  onChange={e => set("name", e.target.value)}
                  autoComplete="name"
                />
              </label>

              <label>
                <input
                  placeholder="Phone"
                  value={form.phone}
                  onChange={e => set("phone", e.target.value)}
                  autoComplete="tel"
                />
              </label>
              <label>
                <input
                  placeholder="Confirm phone"
                  value={form.confirmPhone}
                  onChange={e => set("confirmPhone", e.target.value)}
                  autoComplete="tel"
                />
              </label>

              <label className="span-2">
                <input
                  placeholder="Address"
                  value={form.address}
                  onChange={e => set("address", e.target.value)}
                  autoComplete="street-address"
                />
              </label>

              <label>
                <input
	          name="postcode"
                  placeholder="Postcode"
                  value={form.postcode}
                  onChange={e => set("postcode", e.target.value.toUpperCase())}
                  autoComplete="postal-code"
	 	  inputMode="text"
  	          spellCheck={false}
                />
              </label>

              <label>
                <input
                  value={authority_name || ""}
                  disabled
                  readOnly
                  placeholder="Local authority (auto)"
                />
              </label>

              {/* Cuisine types */}
              <fieldset className="span-2">
                <legend>Cuisine types</legend>
                <div className="pills row-wrap">
                  {CUISINE_OPTIONS.map(c => (
                    <label key={c} className="pill">
                      <input
                        type="checkbox"
                        checked={form.cuisine_types.includes(c)}
                        onChange={() => toggleArrayVal("cuisine_types", c)}
                      />
                      <span>{c}</span>
                    </label>
                  ))}
                </div>
              </fieldset>

              {/* Meal types */}
              <fieldset>
                <legend>Meal types offered</legend>
                <div className="pills row-wrap">
                  {MEAL_TYPES.map(m => (
                    <label key={m} className="pill">
                      <input
                        type="checkbox"
                        checked={form.meal_types.includes(m)}
                        onChange={() => toggleArrayVal("meal_types", m)}
                      />
                      <span>{m}</span>
                    </label>
                  ))}
                </div>
              </fieldset>

              {/* Price categories */}
              <fieldset>
                <legend>Price categories</legend>
                <div className="pills row-wrap">
                  {PRICE_OPTIONS.map(p => (
                    <label key={p} className="pill">
                      <input
                        type="checkbox"
                        checked={form.price_categories.includes(p)}
                        onChange={() => toggleArrayVal("price_categories", p)}
                      />
                      <span>{p}</span>
                    </label>
                  ))}
                </div>
              </fieldset>

              {/* Email */}
		<label className="span-2">
  <input
    type="email"
    name="email"
    value={email}
    readOnly
    disabled
    autoComplete="username"
  />
</label>


              {/* Password + Confirm in same row */}
              <label>
                <input
                  type="password"
                  placeholder="Password"
                  value={form.password}
                  onChange={e => set("password", e.target.value)}
                />
              </label>
              <label>
                <input
                  type="password"
                  placeholder="Confirm password"
                  value={form.confirmPassword}
                  onChange={e => set("confirmPassword", e.target.value)}
                />
              </label>
            </div>

            {msg && <div className="toast error">{msg}</div>}

            <div className="actions">
              <button className="btn" type="submit">Create account</button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
}

